'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { expensesAPI, eventsAPI, exportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TableSkeleton } from '../components/LoadingSkeleton';
import SearchBar from '../components/SearchBar';
import { handleExport } from '../utils/exportHelper';
import '../styles/Expenses.css';

function Expenses() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [expenses, setExpenses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    eventId: '',
    date: '',
    category: 'vegetables',
    amount: '',
    notes: '',
  });

  useEffect(() => {
    fetchExpenses();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await expensesAPI.getAll();
      setExpenses(response.data.expenses);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch expenses';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const getEventName = useCallback((eventId) => {
    const event = events.find(e => e._id === eventId);
    return event ? event.eventName : 'Unknown Event';
  }, [events]);

  // Filter expenses based on search term
  const filteredExpenses = useMemo(() => {
    if (!searchTerm) return expenses;
    const term = searchTerm.toLowerCase();
    return expenses.filter(expense => {
      const eventName = getEventName(expense.eventId._id || expense.eventId).toLowerCase();
      return (
        eventName.includes(term) ||
        expense.category.toLowerCase().includes(term) ||
        expense.notes?.toLowerCase().includes(term) ||
        expense.amount.toString().includes(term)
      );
    });
  }, [expenses, searchTerm, getEventName]);

  // Group expenses by date for daily view
  const expensesByDate = useMemo(() => {
    const grouped = {};
    filteredExpenses.forEach((expense) => {
      const dateKey = new Date(expense.date).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          expenses: [],
          totalAmount: 0,
          byCategory: {},
        };
      }
      grouped[dateKey].expenses.push(expense);
      const amount = Number(expense.amount) || 0;
      grouped[dateKey].totalAmount += amount;
      const cat = expense.category || 'other';
      grouped[dateKey].byCategory[cat] = (grouped[dateKey].byCategory[cat] || 0) + amount;
    });
    return grouped;
  }, [filteredExpenses]);

  const dailyList = useMemo(() => {
    return Object.values(expensesByDate).sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [expensesByDate]);

  const fetchEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data.events);
    } catch (err) {
      console.error('Failed to fetch events');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await expensesAPI.update(editingId, formData);
        success('Expense updated successfully');
      } else {
        await expensesAPI.create(formData);
        success('Expense created successfully');
      }
      setFormData({
        eventId: '',
        date: '',
        category: 'vegetables',
        amount: '',
        notes: '',
      });
      setShowForm(false);
      setEditingId(null);
      fetchExpenses();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save expense';
      showError(message);
    }
  };

  const handleEdit = (expense) => {
    setFormData({
      eventId: expense.eventId._id,
      date: expense.date.split('T')[0],
      category: expense.category,
      amount: expense.amount,
      notes: expense.notes,
    });
    setEditingId(expense._id);
    setShowForm(true);
  };

  const handleDelete = async (id, category, amount) => {
    if (window.confirm(`Are you sure you want to delete this expense (${category} - ₹${amount})?`)) {
      try {
        await expensesAPI.delete(id);
        success('Expense deleted successfully');
        fetchExpenses();
      } catch (err) {
        const message = err.response?.data?.message || 'Failed to delete expense';
        showError(message);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      eventId: '',
      date: '',
      category: 'vegetables',
      amount: '',
      notes: '',
    });
  };

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  const summary = useMemo(() => {
    const totalAmount = totalExpenses;
    const totalDays = dailyList.length;
    const averagePerDay = totalDays ? totalAmount / totalDays : 0;

    let topDay = null;
    dailyList.forEach((day) => {
      if (!topDay || day.totalAmount > topDay.totalAmount) {
        topDay = day;
      }
    });

    return {
      totalAmount,
      totalDays,
      averagePerDay,
      topDayAmount: topDay ? topDay.totalAmount : 0,
      topDayDate: topDay ? topDay.date : null,
    };
  }, [totalExpenses, dailyList]);

  return (
    <div className="expenses-container">
      <div className="page-header">
        <div>
          <h1>Expenses</h1>
          <p className="page-subtitle">
            Track your event expenses by day, category, and notes.
          </p>
        </div>
        <div className="header-actions">
          <div className="view-mode-toggle">
            <button
              onClick={() => setViewMode('daily')}
              className={`btn btn-small ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Daily summary
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`btn btn-small ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            >
              Detailed table
            </button>
          </div>
          {expenses.length > 0 && (
            <button
              onClick={() => handleExport(
                exportAPI.exportExpenses,
                `expenses_${new Date().toISOString().split('T')[0]}.xlsx`,
                () => success('Expenses exported successfully'),
                (error) => showError(error)
              )}
              className="btn btn-secondary"
            >
              Export to Excel
            </button>
          )}
          {user?.role === 'owner' && (
            <button onClick={() => setShowForm(!showForm)} className="btn">
              {showForm ? 'Cancel' : '+ New Expense'}
            </button>
          )}
        </div>
      </div>

      {!loading && expenses.length > 0 && (
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by event, category, amount, or notes..."
        />
      )}

      {showForm && (
        <div className="form-container">
          <h2>{editingId ? 'Edit Expense' : 'Create New Expense'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Event *</label>
                <select
                  name="eventId"
                  value={formData.eventId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Event</option>
                  {events.map(event => (
                    <option key={event._id} value={event._id}>
                      {event.eventName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="vegetables">Vegetables</option>
                  <option value="groceries">Groceries</option>
                  <option value="gas">Gas</option>
                  <option value="transport">Transport</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn">
                {editingId ? 'Update' : 'Create'} Expense
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {!loading && filteredExpenses.length > 0 && (
        <div className="expenses-summary">
          <div className="summary-card">
            <div className="summary-label">Total Days</div>
            <div className="summary-value">{summary.totalDays}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Expenses</div>
            <div className="summary-value">₹{summary.totalAmount.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Average / Day</div>
            <div className="summary-value">₹{summary.averagePerDay.toFixed(2)}</div>
          </div>
          {summary.topDayDate && (
            <div className="summary-card warning">
              <div className="summary-label">Highest Spend Day</div>
              <div className="summary-value">₹{summary.topDayAmount.toFixed(2)}</div>
              <div className="summary-subtext">
                {new Date(summary.topDayDate).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : filteredExpenses.length > 0 ? (
        viewMode === 'daily' ? (
          <div className="expenses-daily-list">
            {dailyList.map((day) => (
              <div key={day.date} className="daily-expense-card">
                <div className="daily-expense-header">
                  <div className="daily-date-info">
                    <div className="daily-date">
                      {new Date(day.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="daily-stats">
                      <span>{day.expenses.length} expense{day.expenses.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="daily-totals">
                    <div className="total-amount">₹{day.totalAmount.toFixed(2)}</div>
                    <div className="amount-breakdown">
                      {Object.entries(day.byCategory).map(([cat, amt]) => (
                        <span key={cat} className="category-total">
                          {cat}: ₹{amt.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="daily-expense-list">
                  {day.expenses.map((expense) => (
                    <div key={expense._id} className="daily-expense-entry">
                      <div className="daily-expense-main">
                        <div className="expense-event">
                          {getEventName(expense.eventId._id || expense.eventId)}
                        </div>
                        <span className={`category category-${expense.category}`}>
                          {expense.category}
                        </span>
                      </div>
                      <div className="daily-expense-amount">
                        ₹{Number(expense.amount || 0).toFixed(2)}
                      </div>
                      {expense.notes && (
                        <div className="daily-expense-notes">{expense.notes}</div>
                      )}
                      {user?.role === 'owner' && (
                        <div className="daily-expense-actions">
                          <button
                            onClick={() => handleEdit(expense)}
                            className="btn btn-small"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDelete(expense._id, expense.category, expense.amount)
                            }
                            className="btn btn-danger btn-small"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="expenses-table-wrapper">
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Notes</th>
                  {user?.role === 'owner' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map(expense => (
                  <tr key={expense._id}>
                    <td>{getEventName(expense.eventId._id || expense.eventId)}</td>
                    <td>{new Date(expense.date).toLocaleDateString()}</td>
                    <td>
                      <span className={`category category-${expense.category}`}>
                        {expense.category}
                      </span>
                    </td>
                    <td>₹{expense.amount.toFixed(2)}</td>
                    <td>{expense.notes}</td>
                    {user?.role === 'owner' && (
                      <td>
                        <button
                          onClick={() => handleEdit(expense)}
                          className="btn btn-small"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(expense._id, expense.category, expense.amount)
                          }
                          className="btn btn-danger btn-small"
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : searchTerm ? (
        <div className="no-data">No expenses found matching "{searchTerm}"</div>
      ) : (
        <div className="no-data">No expenses found. Create your first expense!</div>
      )}
    </div>
  );
}

export default Expenses;
